import { UserIcon } from '@heroicons/react/16/solid';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import apiService from 'src/shared/config/apiService';
import type { BulkOrdering } from 'src/shared/types/bulkOrdering';
import type { ProductById } from 'src/shared/types/product';
import { OrderQuote } from 'src/shared/types/quote';

import ClearcoBanner from 'src/shared/components/ClearcoBanner';
import LMLoading from 'src/shared/components/LMLoading/LMLoading';
import { Tab, TabsV2, Typography } from 'src/shared/components/ui';

import getQuoteSupplierCloaks from '../../utils/getQuoteSupplierCloaks';
import CavelaDirectQuoteSummary from './CavelaDirectQuoteSummary';
import quoteSteps from './constants/quote-steps';
import DottedMapComponent from './DottedMapComponent/DottedMapComponent';
import ProductDetailRfqRejected from './ProductDetailRfqRejected';
import QuotesTable from './QuotesTable';

interface ProductDetailQuotesTabProps {
  product: ProductById;
  data: OrderQuote[];
  isSuccess: boolean;
  /**
   * Bulk orderings for this product. Forwarded to the Cavela Direct summary so
   * it can show a "View order" link once the bulk-order payment is complete.
   */
  bulkOrderings?: BulkOrdering[];
  /** When true, show the Cavela Direct quote summary card (product tag + feature flag). */
  showCavelaDirectQuoteSummary?: boolean;
  /** Opens the shared product RFQ drawer (same one the page header "Product specs" button opens). */
  onOpenRfqDrawer?: () => void;
}

const ProductDetailQuotesTab: React.FC<ProductDetailQuotesTabProps> = ({
  product,
  data,
  isSuccess,
  bulkOrderings,
  showCavelaDirectQuoteSummary = false,
  onOpenRfqDrawer
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [steps, setSteps] = useState(Array.from(quoteSteps));
  const [searchParams] = useSearchParams();
  const doAnimation = searchParams.get('a') === '1';
  const [isLoading, setIsLoading] = useState(true);
  const isRfqRejected = product.approval_status === 'rejected';

  const supplierCloaks = useMemo(() => getQuoteSupplierCloaks(data), [data]);

  const quotes = useMemo(() => {
    if (!data) return [];

    let parsedQuotes = [];

    if (data) {
      // based on v1 - need to make it better for v2
      parsedQuotes = data
        .flatMap((data: any) => {
          const { product_quotes: _, ...rest } = data;

          return data.product_quotes.map((quote: any) => ({
            ...quote,
            ...rest,
            id: quote?.id,
            supplier_name: rest.supplier.cloak,
            supplier_factory_country: rest.supplier.supplier_factory.country,
            variant: quote.product_name,
            price: quote.exw_unit_price_marked_up,
            quantity: quote.quantity,
            production_time: quote.production_time_days,
            ratings: rest.supplier.average_score,
            cavela_review: rest.recommended ? 'best' : null,
            files_length: quote.files.length,
            quote_notes: quote.client_notes,
            staffOnly: rest.status === 'draft' || rest.status === 'ready',
            match_score: rest?.recommended ? 999 : rest?.match_score,
            ui_status: rest.samples?.length > 0 ? 'hasSamples' : 'noSamples'
          }));
        })
        .map((val, index) => ({ key: `${index + 1}`, ...val }))
        .sort((a: any, b: any) => {
          const aDate = Date.parse(a.created_at);
          const bDate = Date.parse(b.created_at);

          if (aDate === bDate) return 0;

          return aDate > bDate ? -1 : 1;
        });
    }

    return parsedQuotes || [];
  }, [data]);

  /** When set, legacy matching progress + QuotesTable are suppressed in favor of the Cavela Direct summary. */
  const showCavelaDirectSummaryBlock = useMemo(
    () =>
      Boolean(
        isSuccess && showCavelaDirectQuoteSummary && product.auto_quote_result
      ),
    [isSuccess, showCavelaDirectQuoteSummary, product.auto_quote_result]
  );

  const renderSupplierCloaks = () => {
    return supplierCloaks.map((cloak) => (
      <Tab
        key={cloak}
        title={
          <div className="flex items-center gap-1">
            <UserIcon className="size-4" />
            <span>{cloak}</span>
          </div>
        }
      />
    ));
  };

  const getQuoting = async () => {
    const data = await apiService.get(`/products/${product.id}/quoting`);
    const stats = data?.data?.stats as any;

    if (!stats) return;

    if (stats?.supplier_contact_estimation > 0) {
      // Step 1: RFQ reviewed, factory matching underway
      if (doAnimation) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      setSteps((prevSteps) =>
        prevSteps.map((step, index) => {
          if (index === 0) {
            return { ...step, done: true, loading: false };
          }
          if (index === 1) {
            return { ...step, loading: true };
          }

          return step;
        })
      );

      if (stats?.quoting_suppliers > 0) {
        // Step 2: factories matched, engagement underway
        setSteps((prevSteps) =>
          prevSteps.map((step, index) => {
            if (index === 1) {
              return { ...step, done: true, loading: false };
            }
            if (index === 2) {
              return { ...step, loading: true };
            }

            return step;
          })
        );

        if (stats?.surfaced_quotes > 0) {
          // Step 3 & 4: production quotes received
          setSteps((prevSteps) =>
            prevSteps.map((step, index) => {
              if (index === 2) {
                return { ...step, done: true };
              }
              if (index === 3) {
                return { ...step, done: true, loading: false };
              }

              return step;
            })
          );
        }
      }
    }
  };

  useEffect(() => {
    if (isRfqRejected) {
      return;
    }

    // Not approved yet: leave the steps in their initial state, where step 1
    // ("We're reviewing your RFQ") is already the in-progress one.
    if (product.approval_status !== 'approved') {
      return;
    }

    getQuoting();
    let replaceUrlInterval: NodeJS.Timeout;

    if (doAnimation) {
      const location = window.location.href;
      const params = new URL(location);

      replaceUrlInterval = setInterval(() => {
        window.history.replaceState(null, '', params.pathname);
      }, 4000);
    }

    return () => {
      setSteps(Array.from(quoteSteps));
      clearInterval(replaceUrlInterval);
    };
  }, [product, doAnimation, isRfqRejected]);

  if (isRfqRejected) {
    return <ProductDetailRfqRejected product={product} />;
  }

  return (
    <div>
      {showCavelaDirectSummaryBlock && (
        <div className="w-fulll">
          <div className="lg:items-start mobile:max-w-full">
            <div className={product.rfq ? 'min-w-0' : 'min-w-0 lg:col-span-2'}>
              <CavelaDirectQuoteSummary
                cardRef={undefined}
                onOpenRfqDrawer={onOpenRfqDrawer}
                product={product}
                orderQuotes={data}
                bulkOrderings={bulkOrderings}
                isProductPage
              />
            </div>
            {/* {product.rfq && (
              <div className="mobile:max-h-full overflow-y-auto overflow-x-hidden cavela-scrollbar max-h-[min(100dvh-172px,720px)]">
                <CavelaDirectRfqTabsPanel
                  stickyTabs
                  rfq={product.rfq}
                  activeTab={cavelaDirectRfqTab}
                  onActiveTabChange={setCavelaDirectRfqTab}
                  timelineOpen={cavelaDirectRfqTab === 'timeline'}
                  className="flex min-h-0 flex-col"
                />
              </div>
            )} */}
          </div>
        </div>
      )}

      {quotes.length === 0 && isSuccess && !showCavelaDirectSummaryBlock && (
        <div>
          <Typography
            className="flex items-center gap-2 h-[33px]"
            variant="h4"
            as="h2"
            weight="bold"
          >
            Quotes
          </Typography>
          <div className="mt-4 mb-2">
            <span className="text-md font-500">Quote Progress</span>
          </div>
          <div className="w-full p-2 border border-border-light-gray-2 rounded-lg">
            <div className="flex flex-col">
              {steps.map((step, index) => (
                <div key={index}>
                  <div className="flex items-center text-black">
                    <div
                      className={`p-3 rounded-xl shrink-0 ${step.loading || step.done ? 'border-2 border-[#030DEF] bg-[#030DEF]' : 'border-2 border-[#CBCFFD] bg-[#CBCFFD]'} ${doAnimation ? 'transition-all duration-300 delay-300' : ''}`}
                    >
                      <img
                        src={step.icon}
                        alt={step.placeholder}
                        className={`size-4 ${step.loading || step.done ? 'opacity-100' : 'opacity-50'}`}
                      />
                    </div>
                    <div
                      className={`flex items-center ml-2 ${step.loading || step.done ? 'opacity-100' : 'opacity-50'} ${doAnimation ? 'transition-all duration-300 delay-[400ms]' : ''}`}
                    >
                      <div className="flex gap-1 flex-col">
                        <div className="flex">
                          <span className="font-500">{step.placeholder}</span>{' '}
                          {step.loading && (
                            <div className="ml-2 relative top-[-6px]">
                              <LMLoading small={true} />
                            </div>
                          )}
                        </div>
                        <span className="text-light-gray">
                          {step.description}
                        </span>
                      </div>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="relative w-11 my-2 flex items-center justify-center">
                      {step.done && doAnimation ? (
                        <motion.span
                          className="absolute top-0 w-1 h-6 bg-[#030DEF]  rounded-full"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 24, opacity: 1 }}
                          transition={{ type: 'spring', stiffness: 220 }}
                        />
                      ) : null}
                      <span
                        className={`w-1 h-6 bg-[#030DEF] rounded-full ${step.done && !doAnimation ? 'opacity-100' : 'opacity-30'}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="relative max-h-[400px] rounded-md overflow-hidden mt-4 flex items-center justify-center">
              <DottedMapComponent
                productName={product?.detail.name || 'Your product'}
                image={product?.rfq?.image || ''}
                isFromAsia={false}
              />
            </div>
          </div>
        </div>
      )}

      {isSuccess && quotes.length > 0 && !showCavelaDirectSummaryBlock && (
        <div>
          <Typography
            className="flex items-center gap-2 h-[33px]"
            variant="h4"
            as="h2"
            weight="bold"
          >
            {isSuccess && (
              <>
                {quotes?.length} {quotes?.length === 1 ? 'Quote' : 'Quotes'}
              </>
            )}
          </Typography>

          <div className="mt-3">
            <TabsV2
              selectedKey={selectedSupplier}
              onSelectionChange={(key) => setSelectedSupplier(key as string)}
              color="dark"
              variant="light"
              classNames={{ tabList: '!px-0' }}
            >
              <Tab key="all" title="All" />
              {renderSupplierCloaks()}
            </TabsV2>
          </div>

          <QuotesTable
            key={`quotes-table-${product?.id}`}
            isLoading={isLoading}
            quotes={quotes}
            product={product}
            setIsLoading={setIsLoading}
            selectedSupplier={selectedSupplier}
          />

          {!isLoading && <ClearcoBanner />}
        </div>
      )}
    </div>
  );
};

export default ProductDetailQuotesTab;
